import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { ReactComponent as DeleteIcon } from '../../../../assets/icons/delete-icon.svg';
import { ReactComponent as EditIcon } from '../../../../assets/icons/edit.svg';
import { ReactComponent as LocationIcon } from '../../../../assets/icons/location-icon.svg';
import { ReactComponent as PlusIcon } from '../../../../assets/icons/plus.svg';
import { ReactComponent as StationIcon } from '../../../../assets/icons/sidebar/sidebar-account.svg';
import { ReactComponent as ExpandIcon } from '../../../../assets/icons/tables/expand-icon.svg';
import { ReactComponent as NewTabIcon } from '../../../../assets/icons/tables/new-tab.svg';
import DefaultApiLimit from '../../../../config/defaultLimit';
import { PortalType } from '../../../../config/enum';
import { usePortal } from '../../../../contexts/PortalContext/PortalContext';
import {
  AvailableStation,
  RequestStationInterface,
} from '../../../../interface/partner-interfaces/location-management.interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import { setPageTitle } from '../../../../redux/slices/PageTitleSlice';
import {
  disableFiltersApplied,
  fetchApprovedStations,
  fetchListFilters,
  setApprovedLocationFilterData,
  setSortData,
  updateLocationFilters,
  refreshApprovedStations,
} from '../../../../redux/slices/Partners/ApprovedLocationManagementSlice';
import { RootState } from '../../../../redux/store';
import MyButton from '../../../../shared-components/button/Button';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import ModalBox from '../../../../shared-components/modal-box/ModalBox';
import DeviceSidePanel from '../../../../shared-components/table-side-panel/DeviceSidePanel';
import FacilityDetailsSidePanel from '../../../../shared-components/table-side-panel/FacilityDetailsSidePanel';
import { useTableSidePanel } from '../../../../shared-components/table-side-panel/hooks/useTableSidePanel';
import LocationSidePanel from '../../../../shared-components/table-side-panel/LocationSidePanel';
import { GetParametersFromCommonFilters } from '../../../../shared-functions/CommonFilters';
import ConvertUTCtoDateTime from '../../../../shared-functions/ConvertUTCtoDateTime';
import { removeEmptyKeys } from '../../../../shared-functions/DataTime';
import { exportCSV, exportPDF } from '../../../../shared-functions/ExportFile';
import FormatUtcString from '../../../../shared-functions/FormatUtcString';
import { permissionCheck } from '../../../../shared-functions/JWTDocodeFunction';
import stationApi from '../../../../utils/api/partner/station';

import AddLocation from './AddLocation';

interface ModelBoxDetailsInterface {
  title: string;
  header: boolean;
  status: boolean;
  content: React.JSX.Element;
}

interface ApprovedListProperties {
  renderAvailability: (value: AvailableStation) => JSX.Element;
  preventNavigation?: boolean;
  fetchByPartner?: string;
  updateCounts?: () => void;
  onSuccess?: () => void;
}

/**
 * Add new station component
 * @param {Readonly<ApprovedListProperties>} param0 - The component properties
 * @param {Function} param0.renderAvailability - The renderAvailability prop
 * @param {boolean} [param0.preventNavigation] - The preventNavigation prop
 * @param {string} [param0.fetchByPartner] - Account custom ID to filter stations by, when scoped to a single partner
 * @param {() => void} [param0.updateCounts] - The updateCounts callback prop
 * @param {() => void} [param0.onSuccess] - The onSuccess callback prop
 * @returns {any} Approved list component
 */
function ApprovedList({
  renderAvailability,
  preventNavigation = false,
  fetchByPartner,
  updateCounts,
  onSuccess,
}: Readonly<ApprovedListProperties>) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('location');
  const isPartnerPortal = localStorage.getItem('activePortal') === 'partner';
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const {
    stations,
    page,
    limit,
    loading,
    totalPages,
    totalResults,
    filters,
    filterListLoading,
    isFilterUpdated,
    sortBy,
    sortField,
  } = useAppSelector((state: RootState) => state.partnersApprovedLocation);
  const { currentFacilityTagsList } = useAppSelector((state) => state.partnersFacilityTags);
  const [searchParameters, setSearchParameters] = useSearchParams();

  /**
   * Navigates to the station dashboard.
   * @param {any} stationId The station custom ID
   */
  const handleExpandStation = (stationId: string | any) => {
    const stationDetails = stations.find((value: AvailableStation) => value._id === stationId);
    if (!stationDetails) return;
    navigate(
      `/ev-admin/location-management/${stationDetails._id}/location-dashboard?searchBy=${stationDetails.name}`,
      { state: { source: window.location.pathname + window.location.search } },
    );
  };

  /**
   * Opens the station dashboard in a new tab.
   * @param {any} stationId The station custom ID
   */
  const handleOpenStationInNewTab = (stationId: string | any) => {
    const stationDetails = stations.find((value: AvailableStation) => value._id === stationId);
    if (!stationDetails) return;
    window.open(
      `/ev-admin/location-expanded/${stationDetails._id}/location-dashboard?searchBy=${stationDetails.name}&source=${window.location.pathname}${window.location.search}`,
      '_blank',
    );
  };
  // const [currentRowData, setCurrentRowData] = useState<AvailableStation | null>(null);
  const [sidePanelOpen, setSidePanelOpen] = useState<boolean>(false);
  const [modalBoxDetail, setModalBoxDetail] = useState<ModelBoxDetailsInterface>({
    title: '',
    header: false,
    status: false,
    content: <div />,
  });

  // const [selectedStationId, setSelectedStationId] = useState<string | null>(null);

  const { page: pageParameter } = useParams<{ page: string }>();
  const { portalType } = usePortal();

  /**
   * Handle the fetch the data function
   * @param {any} data - The data
   */
  const fetchData = (data: RequestStationInterface) => {
    const selectedFacility = currentFacilityTagsList.find((item) => item.isSelected); // temporarily
    console.log(selectedFacility?._id, portalType, PortalType);

    if (!loading) {
      dispatch(
        fetchApprovedStations({
          ...data,
          ...(fetchByPartner ? { accountCustomIds: [fetchByPartner] } : {}),
        }),
      );
    }
  };

  /**
   * Handle the filter data clear function
   * @param {any} sortData - The sortData
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      setApprovedLocationFilterData({
        ...sortData,
      }),
    );
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };
  // /**
  //  * Function to handle page change
  //  * @param pageNumber
  //  */
  // const handlePageChange = (pageNumber: number) => {
  //   const queryParameters = GetParametersFromCommonFilters(filters);
  //   fetchData({
  //     ...queryParameters,
  //     sortField,
  //     sortBy,
  //     page: pageNumber,
  //     limit,
  //   });
  // };

  /**
   * Function to handle data limit change
   * @param {any} value - The value
   */
  const handleLimitChange = (value: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      page: DefaultApiLimit.page,
      ...value,
      sortBy,
      sortField,
    });
  };
  /**
   * Auto refresh the table data
   */
  const handleAutoRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);

    dispatch(
      refreshApprovedStations({
        ...queryParameters,
        page,
        limit,
        sortBy,
        sortField,
        ...(fetchByPartner ? { accountCustomIds: [fetchByPartner] } : {}),
      }),
    );
  };
  /**
   * Current columns for export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('stationName'), accessorKey: 'name' },
    {
      label: t('facilityName'),
      accessorKey: 'accountName',
    },
    {
      label: t('facilityAccountId'),
      accessorKey: 'accountCustomId',
    },
    { label: t('address'), accessorKey: 'address' },
    { label: t('chargerOnline'), accessorKey: 'onlineChargersCount' },
    { label: t('chargerOffline'), accessorKey: 'offlineChargersCount' },
    { label: t('publiclyAccessible'), accessorKey: 'isAvailable' },
    { label: t('created'), accessorKey: 'createdAt' },
    { label: t('lastUpdated'), accessorKey: 'updatedAt' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export data function
   * @param {string} type - The export type ('pdf' or 'csv')
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);
    const queryParameters = GetParametersFromCommonFilters(filters);
    stationApi
      .fetchStations(
        removeEmptyKeys({
          ...queryParameters,
          submissionStatus: 'approved',
          sortBy,
          sortField,
          page: 1,
          limit: totalResults || 1000,
          ...(fetchByPartner ? { accountCustomIds: [fetchByPartner] } : {}),
        }),
      )
      .then((response) => {
        const stationsList = (response.data?.results || response.data || []) as AvailableStation[];
        // Convert data to CSV format
        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = stationsList.map((row: AvailableStation, index: number) =>
          exportCurrentColumns.map((col) => {
            const {
              opensAt,
              closesAt,
              isAvailable,
              createdAt,
              updatedAt,
              is_available: isAvailableSnake,
              available,
              online,
              onlineChargers,
              onlineChargersCount,
              offline,
              offlineChargers,
              offlineChargersCount,
              created_at: createdAtSnake,
              created,
              updated_at: updatedAtSnake,
              lastUpdated,
            } = row as any;
            const key = col.accessorKey;
            switch (key) {
              case '__serial__':
                return String(index + 1);
              case 'opensAt':
                return `${FormatUtcString(opensAt)} - ${FormatUtcString(closesAt)}`;
              case 'isAvailable': {
                let resolvedAvailable = isAvailable;
                if (resolvedAvailable === undefined || resolvedAvailable === null) {
                  const altAvailable = isAvailableSnake ?? available;
                  resolvedAvailable =
                    altAvailable !== undefined ? (altAvailable as boolean) : false;
                }
                return resolvedAvailable !== false ? t('available') : t('unavailable');
              }
              case 'onlineChargersCount': {
                let resolvedOnline = onlineChargersCount;
                if (
                  resolvedOnline === undefined ||
                  resolvedOnline === null ||
                  resolvedOnline === ''
                ) {
                  resolvedOnline = online ?? onlineChargers ?? '0';
                }
                return resolvedOnline !== undefined &&
                  resolvedOnline !== null &&
                  resolvedOnline !== ''
                  ? String(resolvedOnline)
                  : '0';
              }
              case 'offlineChargersCount': {
                let resolvedOffline = offlineChargersCount;
                if (
                  resolvedOffline === undefined ||
                  resolvedOffline === null ||
                  resolvedOffline === ''
                ) {
                  resolvedOffline = offline ?? offlineChargers ?? '0';
                }
                return resolvedOffline !== undefined &&
                  resolvedOffline !== null &&
                  resolvedOffline !== ''
                  ? String(resolvedOffline)
                  : '0';
              }
              case 'createdAt': {
                const dateValue =
                  createdAt || (createdAtSnake as string) || (created as string) || '';
                return ConvertUTCtoDateTime(dateValue) || '-';
              }
              case 'updatedAt': {
                const dateValue =
                  updatedAt || (updatedAtSnake as string) || (lastUpdated as string) || '';
                return ConvertUTCtoDateTime(dateValue) || '-';
              }
              default: {
                const value = (row as any)[key];
                return value !== undefined && value !== null && value !== ''
                  ? String(value)
                  : 'N/A';
              }
            }
          }),
        );
        setExportLoader(false);
        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'approved-station-list.csv');
          } else {
            exportPDF(headers, rows, 'approved-station-list.pdf');
          }
        }
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            status: true,
            type: 'error',
            message: error?.response?.data?.message || error?.message || t('somethingWentWrong'),
            code: error?.response?.data?.code || error?.response?.status || 0,
          }),
        );
      });
  };

  /**
   * Function for closing model
   */
  const handleCloseModel = () => {
    setModalBoxDetail((previous) => ({
      ...previous,
      status: false,
    }));
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      page,
      limit,
      sortBy,
      sortField,
    });
    if (updateCounts) {
      updateCounts();
    }
  };

  /**
   * Add account function
   */
  const addStation = () => {
    const parameters = new URLSearchParams(searchParameters);
    parameters.delete('stationId'); // close row panel if open
    parameters.delete('accountCustomId'); // close row panel if open
    setSearchParameters(parameters);

    setSidePanelOpen(true);
  };

  const {
    isEditMode,
    openStatus,
    setOpenStatus,
    modalView,
    handleEditModeChange,
    handleSidePanelClose,
    renderSidePanelTopLeftContent,
    handleDeleteStationClick,
  } = useTableSidePanel({
    onRefresh: handleRefresh,
    sessions: stations,
  });

  /**
   * Handle the open side panel on row click function
   * @param {any} _ - row click parameter
   * @param {any} row - The facility tag data
   */
  const handleOpenSidePanelOnRowClick = (_: string, row: AvailableStation) => {
    if (!row?._id) return;

    handleEditModeChange(false);

    const parameters = new URLSearchParams(searchParameters);
    parameters.delete('accountCustomId');
    parameters.set('stationId', row._id);
    setSearchParameters(parameters);

    setSidePanelOpen(false);
    // setCurrentRowData(row);
  };

  const column = [
    {
      label: t('stationName'),
      accessorKey: 'name' as keyof AvailableStation,
      sortAllow: true,
      /**
       * No of stations
       * @param {any} currentRow - The currentRow
       */
      customData: (currentRow: AvailableStation) => (
        <p className="hyper-link-coloum">{currentRow.name || '-'}</p>
      ),
    },
    ...(!isPartnerPortal
      ? [
          {
            label: t('facilityName'),
            accessorKey: 'accountName' as keyof AvailableStation,
            sortAllow: true,
            /**
             * Facility name view in table
             * @param row
             * @returns
             */
          },
          {
            label: t('facilityAccountId'),
            accessorKey: 'accountCustomId' as keyof AvailableStation,
            sortAllow: true,
            queryParamsKey: 'accountCustomId',
            queryParamsValue: 'accountCustomId',
            sidePanelComponent: (
              <FacilityDetailsSidePanel
                actionActive
                isEditMode={isEditMode}
                onEditModeChange={handleEditModeChange}
                fetchIdFrom="queryParams"
                onSuccess={() => {
                  handleEditModeChange(false);
                  handleRefresh();
                }}
                onRefreshList={handleRefresh}
              />
            ),
            disableNewTab: true,
          },
        ]
      : []),
    {
      label: t('Address'),
      accessorKey: 'address' as keyof AvailableStation,
      sortAllow: false,
      columnWidth: 200,
      /**
       * Location view in table
       * @param {any} station - The station
       */
      customData: (station: AvailableStation) =>
        station.address ? (
          <div className="location-in-station-table">
            <span>
              <LocationIcon />
            </span>
            <p className="table-row-each-data">{station.address}</p>
          </div>
        ) : null,
    },
    {
      label: t('chargerOnline'),
      accessorKey: 'onlineChargersCount' as keyof AvailableStation,
      columnWidth: 120,
      /**
       * Custom Data for online chargers count
       * @param {any} station - The station
       */
      customData: (station: AvailableStation) => <div>{station.onlineChargersCount || '0'}</div>,
    },
    {
      label: t('chargerOffline'),
      accessorKey: 'offlineChargersCount' as keyof AvailableStation,
      columnWidth: 110,
      /**
       * Custom Data for online chargers count
       * @param {any} station - The station
       */
      customData: (station: AvailableStation) => <div>{station.offlineChargersCount || '0'}</div>,
    },
    {
      label: t('publiclyAccessible'),
      accessorKey: 'isAvailable' as keyof AvailableStation,
      customData: renderAvailability,
    },
    {
      label: t('actions'),
      accessorKey: '_id' as keyof AvailableStation,
      columnWidth: 160,
      sortAllow: false,
      hideWhenSidePanelOpen: true,
      sticky: 'right' as const,
      /**
       * Data UI in table
       * @param {any} station - The station data
       */
      customData: (station: AvailableStation) => (
        <div className="action-buttons-wrap">
          {station?._id && (
            <>
              <div
                role="presentation"
                className="action-button"
                onClick={(event) => {
                  event.stopPropagation();
                  handleExpandStation(station._id);
                }}>
                <ExpandIcon className="icon expand" />
              </div>
              <div
                role="presentation"
                className="action-button"
                onClick={(event) => {
                  event.stopPropagation();
                  handleOpenStationInNewTab(station._id);
                }}>
                <NewTabIcon className="icon new-tab" />
              </div>
              <div
                role="presentation"
                className={`action-button ${
                  !permissionCheck('partnerChargingStationUpdate') &&
                  !permissionCheck('cpoChargingStationUpdate')
                    ? 'disable-action'
                    : ''
                }`}
                onClick={(event) => {
                  event.stopPropagation();

                  if (
                    !permissionCheck('partnerChargingStationUpdate') &&
                    !permissionCheck('cpoChargingStationUpdate')
                  ) {
                    return;
                  }
                  handleEditModeChange(true);
                  const parameters = new URLSearchParams(searchParameters);
                  parameters.delete('accountCustomId');
                  parameters.set('stationId', station._id as string);
                  parameters.set('action', 'edit');
                  setSearchParameters(parameters);
                  setSidePanelOpen(false);
                  // setCurrentRowData(station);
                }}>
                <EditIcon className="icon edit" />
              </div>
              <div
                role="presentation"
                className={`action-button ${
                  !permissionCheck('partnerChargingStationDelete') &&
                  !permissionCheck('cpoChargingStationDelete')
                    ? 'disable-action'
                    : ''
                }`}
                onClick={(event) => {
                  event.stopPropagation();
                  if (
                    !permissionCheck('partnerChargingStationDelete') &&
                    !permissionCheck('cpoChargingStationDelete')
                  ) {
                    return;
                  }
                  handleDeleteStationClick(station._id);
                }}>
                <DeleteIcon className="icon delete" />
              </div>
            </>
          )}
        </div>
      ),
    },
  ];

  const previousFetchByPartner = useRef<string | undefined>();
  const isPartnerDetailsPage = document.location.pathname.includes('accounts/partners/details');

  /**
   * Handling dynamic table heights
   * @returns {string} returns dynamic table height
   */
  const handleHeight = (): number => {
    if (isPartnerDetailsPage) {
      return 500;
    }

    return 330;
  };

  useEffect(() => {
    const hasPartnerChanged = previousFetchByPartner.current !== fetchByPartner;

    if (
      isFilterUpdated ||
      stations.length === 0 ||
      hasPartnerChanged
    ) {
      const queryParameters = GetParametersFromCommonFilters(filters);

      fetchData({
        ...queryParameters,
        page: DefaultApiLimit.page,
        limit: DefaultApiLimit.limit,
      });

      dispatch(disableFiltersApplied());
      previousFetchByPartner.current = fetchByPartner;
    }
  }, [currentFacilityTagsList, filters, fetchByPartner]);

  useEffect(() => {
    if (preventNavigation) {
      return;
    }
    const hasSidePanelParameter = searchParameters.get('stationId');

    if (!hasSidePanelParameter) {
      dispatch(
        setPageTitle({
          title: t('chargingStations'),
          icon: <StationIcon />,
          count: totalResults,
        }),
      );
    } else {
      dispatch(
        setPageTitle({
          title: t('chargingStations'),
          icon: <StationIcon />,
          count: undefined,
        }),
      );
    }
  }, [totalResults, searchParameters, t, preventNavigation]);

  useEffect(() => {
    if (pageParameter === 'add') {
      setSidePanelOpen(true);
    }
  }, [pageParameter]);

  useEffect(() => {
    if (
      searchParameters.get('stationId') ||
      searchParameters.get('deviceId') ||
      (!window.location.pathname.includes('payout-management') &&
        searchParameters.get('accountCustomId'))
    ) {
      const parameters = new URLSearchParams(searchParameters);
      parameters.delete('stationId');
      parameters.delete('deviceId');
      if (!window.location.pathname.includes('payout-management')) {
        parameters.delete('accountCustomId');
      }
      setSearchParameters(parameters, { replace: true });
    }
  }, []);
  return (
    <>
      <ModalBox
        title={modalBoxDetail?.title}
        header={modalBoxDetail?.header}
        open={modalBoxDetail?.status}
        closeModal={handleCloseModel}
        content={modalBoxDetail?.content}
      />
      <ModalBox
        title={modalView.title}
        header={modalView.header}
        open={openStatus}
        closeModal={() => setOpenStatus(false)}
        content={modalView.content}
      />
      <div className="approved-list-table-container" dir={i18n.dir()}>
        <CustomTable
          page={page}
          dataKey="StationApproved"
          loading={loading}
          limit={limit}
          header={column}
          data={stations}
          height={handleHeight()}
          sortData={{
            sortBy,
            sortField,
          }}
          sizeChangeButtonRequired
          handleSortChange={handleSortFunction}
          totalPages={totalPages}
          totalResults={totalResults}
          // handlePageChange={handlePageChange}
          pageLimitChange={handleLimitChange}
          exportDataPdfOrCSV={
            permissionCheck('partnerChargingStationExport') ||
            permissionCheck('cpoChargingStationExport')
              ? exportDataPdfOrCSV
              : undefined
          }
          exportLoader={exportLoader}
          exportData={stations}
          filters={filters}
          filtersToDispatch={updateLocationFilters}
          filterListLoading={filterListLoading}
          filterListToDispatch={fetchListFilters}
          refreshRequired
          refershCurrentPages={handleRefresh}
          hoverLabel={t('refresh')}
          serialNo
          created
          lastUpdated
          onHiddenColumnsChange={setHiddenColumns}
          rowClickEvent={handleOpenSidePanelOnRowClick}
          rowClickParamsKey={searchParameters.get('deviceId') ? 'deviceId' : 'stationId'}
          rowClickSidePanel={
            searchParameters.get('deviceId') ? (
              <DeviceSidePanel
                fetchIdFrom="queryParams"
                isEditMode={isEditMode}
                onEditModeChange={handleEditModeChange}
                onSuccess={() => {
                  handleEditModeChange(false);
                  handleRefresh();
                }}
              />
            ) : (
              <LocationSidePanel
                fetchIdFrom="queryParams"
                initialIsEditMode={isEditMode}
                onEditModeChange={handleEditModeChange}
                onDeleteStation={handleDeleteStationClick}
                onSuccess={() => {
                  handleEditModeChange(false);
                  handleRefresh();
                  onSuccess?.();
                }}
              />
            )
          }
          sidePanelEnabled={sidePanelOpen}
          sidePanelManualContent={
            <AddLocation
              onClose={() => setSidePanelOpen(false)}
              onSuccess={() => {
                handleRefresh();
                onSuccess?.();
              }}
              fetchByPartner={fetchByPartner}
            />
          }
          explore={false}
          onSidePanelClose={() => {
            handleSidePanelClose();
            setSidePanelOpen(false);
          }}
          sidePanelTopLeftContent={
            sidePanelOpen ? (
              <p className="side-panel-header-title">{t('addStation')}</p>
            ) : (
              (renderSidePanelTopLeftContent() ?? undefined)
            )
          }
          rightTopContent={
            <div className="table-bottom-button-list">
              <div className="table-button-item">
                <MyButton
                  label={t('addStation')}
                  buttonType="submit"
                  onClickFunc={addStation}
                  svgIcon={<PlusIcon />}
                  disableButton={
                    !permissionCheck('partnerChargingStationAdd') &&
                    !permissionCheck('cpoChargingStationAdd')
                  }
                />
              </div>
            </div>
          }
          requiredRefreshToggle
          refreshFunction={handleAutoRefresh}
        />
      </div>
    </>
  );
}

ApprovedList.defaultProps = {
  preventNavigation: false,
};

export default ApprovedList;
